import { motion, useMotionValue, useTransform } from "framer-motion";
import { Check } from "lucide-react";
import { useLeadModals } from "@/components/LeadModals";
import { OfferCountdown } from "@/components/home/OfferCountdown";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "20+", label: "Real estate teams" },
  { value: "48h", label: "Avg. turnaround" },
  { value: "80%", label: "Cheaper than crews" },
  { value: "4/mo", label: "Tours & clips included" },
];

export const Hero = () => {
  const { openTrial, openDemo } = useLeadModals();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(my, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(mx, [-0.5, 0.5], [-9, 9]);
  const shiftX = useTransform(mx, [-0.5, 0.5], [-14, 14]);
  const shiftY = useTransform(my, [-0.5, 0.5], [-10, 10]);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <section
      className="relative min-h-screen overflow-hidden bg-white pt-[72px]"
      onMouseMove={onMouseMove}
      data-testid="hero-section"
    >
      <div className="dot-grid pointer-events-none absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="pointer-events-none absolute -right-40 top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full bg-hero-gold/20" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 pb-20 pt-14 sm:px-6 lg:grid-cols-12 lg:gap-6 lg:px-8 lg:pt-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-7 flex flex-wrap items-center gap-x-5 gap-y-2"
            data-testid="hero-trust-signals"
          >
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-hero-black/80">
              <Check className="h-4 w-4 text-hero-blue" strokeWidth={3} /> Trusted by 20+ real estate teams
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-hero-black/80">
              <Check className="h-4 w-4 text-hero-blue" strokeWidth={3} /> No credit card required
            </span>
          </motion.div>

          <h1 className="font-display text-5xl font-bold uppercase leading-[0.98] tracking-tighter text-hero-black sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.2}>Beautiful Real</MaskedLine>
            <MaskedLine delay={0.32}>Estate Videos in</MaskedLine>
            <MaskedLine delay={0.44}>
              <span className="bg-hero-gold px-3 text-hero-black shadow-[6px_6px_0_#111111]">48 Hours</span>
            </MaskedLine>
            <MaskedLine delay={0.56}>
              <span className="text-outline-blue">No Crew Needed</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-hero-black/70 sm:text-lg"
            data-testid="hero-subheadline"
          >
            The 360 in our name stands for the full-circle offer: 360° room tours, drone flights, and promo clips —
            custom-made for your company. 4 productions per month, ready in 48 hours. Cancel anytime.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-col gap-4 sm:flex-row"
          >
            <button
              onClick={openTrial}
              data-testid="hero-cta-trial"
              className="rounded-md bg-hero-gold px-8 py-4 font-display text-lg font-semibold uppercase tracking-wide text-hero-black shadow-[5px_5px_0_#111111] transition-all duration-300 hover:-translate-y-1 hover:shadow-[8px_8px_0_#111111]"
            >
              Start the First Month 50% Cheaper
            </button>
            <button
              onClick={openDemo}
              data-testid="hero-cta-demo"
              className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-hero-blue px-8 py-4 font-display text-lg font-semibold uppercase tracking-wide text-hero-blue transition-all duration-300 hover:-translate-y-1 hover:bg-hero-blue hover:text-white"
            >
              Unsubscribe Every Month
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-6"
          >
            <OfferCountdown />
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 grid max-w-xl grid-cols-2 gap-6 border-t-2 border-hero-black/10 pt-8 sm:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <dt className="sr-only">{s.label}</dt>
                <dd className="font-display text-3xl font-bold text-hero-blue">{s.value}</dd>
                <dd className="mt-1 text-xs font-semibold uppercase tracking-wide text-hero-black/60">{s.label}</dd>
              </div>
            ))}
          </motion.dl>
        </div>

        <div className="relative flex items-center justify-center lg:col-span-5">
          <motion.div
            className="absolute h-72 w-72 rounded-full bg-hero-gold sm:h-96 sm:w-96"
            animate={{ scale: [1, 1.07, 1] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
          />
          <motion.div
            className="absolute h-72 w-72 rounded-full border-2 border-hero-black/20 sm:h-96 sm:w-96"
            animate={{ scale: [1.08, 1, 1.08] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            style={{ rotateX, rotateY, x: shiftX, y: shiftY, transformPerspective: 1000 }}
          >
            <motion.img
              src="/logo-transparent.webp"
              alt="RealEstateHero360 superhero mascot shield"
              className="relative w-80 drop-shadow-[0_25px_35px_rgba(17,17,17,0.35)] sm:w-[30rem]"
              animate={{ y: [0, -16, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              data-testid="hero-logo-shield"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
